import { IPersonalAddressState } from '@interfaces/mfe/mfe-personal-address.interface';
import { AccountTypes } from '@interfaces/models/account-types';
import { InvestmentObjectives } from '@interfaces/models/investment-objectives-types';
import { IRAOptions, IRATypes } from '@interfaces/models/ira-types';
import { VgEmploymentState } from '@interfaces/models/open-account-mfes/employment-mfe.model';
import { SettlementFundAoAEnum } from '@interfaces/settlement-fund/settlement-fund.enum';
import { createSelector } from '@ngrx/store';
import { TransactionState } from '@store/reducers';
import { DecedentInfoState } from '@store/reducers/decedent-info/decedent-info.reducer';
import { FinancialDetailsState } from '@store/reducers/income-net-worth/income-net-worth.reducer';
import { InheritedIraState } from '@store/reducers/inherited-ira/inherited-ira.reducer';
import { MfePersonalInfoClientState } from '@store/reducers/mfe-personal-info-client/mfe-personal-info-client.reducer';
import { MfePhoneState } from '@store/reducers/personal-phone/personal-phone.reducer';
import { selectIncomeNetWorth, selectInheritedIraState } from '@store/selectors';
import { selectDecedentInfoState } from '@store/selectors/decedent-info/decedent-info.selectors';
import { selectInternalTransferFromAccountType } from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { selectInvestmentObjectives } from '@store/selectors/investment-objectives/investment-objectives.selectors';
import { selectMfePersonalInfoClientPersonalInfo } from '@store/selectors/mfe-personal-info-client/mfe-personal-info-client.selectors';
import { selectMfePersonalInfoClientSSN } from '@store/selectors/mfe-personal-info-client/mfe-personal-info-client.selectors';
import {
  selectEmploymentMfeDataByOwnerIndex,
  selectSettlementFundChoice,
} from '@store/selectors/open-account/open-account.selectors';
import { selectMfePersonalAddress } from '@store/selectors/personal-info-address/personal-info-address.selectors';
import { selectMfePhone } from '@store/selectors/personal-phone/personal-phone.selectors';
import {
  selectIraOption,
  selectIraType,
} from '@store/selectors/selected-client-accounts/selected-client-accounts.selectors';
import {
  selectCurrentTransaction,
  selectMultiAccountType,
  selectMultiIraOption,
  selectMultiIraType,
} from '@store/selectors/transaction-state/transaction-state.selectors';
import {
  selectAccountType,
  selectIsInheritedIraFlow,
  selectIsMultiInheritanceFlow,
  selectIsProspect,
} from '@store/selectors/triage/triage.selectors';
import {
  selectEmploymentFeature,
  selectFinancialsState,
  selectInvestmentObjectivesState,
  selectIsPersonalInfoSSN,
} from 'vg-interaction-model';

import clientDataOAMapper from './oa-helper';

export interface ITransferTypes {
  accountType: AccountTypes;
  iraType?: IRATypes;
  iraOption?: IRAOptions;
}

type PersonalInfoClientState = MfePersonalInfoClientState['personalInfo'] & {
  ssn_private: string;
};

export interface PersonalStates {
  personalInfo: PersonalInfoClientState;
  personalPhone: MfePhoneState;
  decedentInfo?: DecedentInfoState;
}

export const selectOATransactionTypes = createSelector(
  selectAccountType,
  selectIraType,
  selectIraOption,
  (accountType: AccountTypes, iraType: IRATypes, iraOption: IRAOptions): ITransferTypes => {
    return {
      accountType,
      iraType,
      iraOption,
    };
  },
);

export const selectInheritedIraTransactionTypes = createSelector(
  selectInheritedIraState,
  (inheritedIraState: InheritedIraState): ITransferTypes => {
    const { isRothIra } = inheritedIraState;
    return {
      accountType: AccountTypes.IRA,
      iraType: isRothIra ? IRATypes.INHERITED_ROTH : IRATypes.INHERITED_OTHER,
      iraOption: IRAOptions.INHERIT,
    };
  },
);

export const selectITOATransactionTypes = createSelector(
  selectInternalTransferFromAccountType,
  (accountType: AccountTypes): ITransferTypes => {
    return {
      accountType,
    };
  },
);

export const selectMultiTransactionTypes = createSelector(
  selectMultiAccountType,
  selectMultiIraType,
  selectMultiIraOption,
  (
    accountType: AccountTypes | null,
    iraType: IRATypes | null,
    iraOption: IRAOptions | null,
  ): ITransferTypes => {
    const result: ITransferTypes = {
      accountType: accountType ?? AccountTypes.EMPTY,
    };

    if (iraType) {
      result.iraType = iraType;
    }

    if (iraOption) {
      result.iraOption = iraOption;
    }

    return result;
  },
);

export const selectPersonalStates = createSelector(
  selectMfePersonalInfoClientPersonalInfo,
  selectMfePersonalInfoClientSSN,
  selectMfePhone,
  selectDecedentInfoState,
  (
    personalInfo: MfePersonalInfoClientState['personalInfo'],
    ssn_private: string,
    personalPhone: MfePhoneState,
    decedentInfo: DecedentInfoState,
  ): PersonalStates => {
    return {
      personalInfo: { ...personalInfo, ssn_private },
      personalPhone: personalPhone,
      decedentInfo: decedentInfo,
    };
  },
);

export const selectITPersonalStates = createSelector(
  selectMfePersonalInfoClientPersonalInfo,
  selectMfePersonalInfoClientSSN,
  selectMfePhone,
  (
    personalInfo: MfePersonalInfoClientState['personalInfo'],
    ssn_private: string,
    personalPhone: MfePhoneState,
  ): PersonalStates => {
    return {
      personalInfo: { ...personalInfo, ssn_private },
      personalPhone: personalPhone,
    };
  },
);

export const selectOAFromStore = createSelector(
  selectPersonalStates,
  selectMfePersonalAddress,
  selectEmploymentMfeDataByOwnerIndex(0),
  selectIncomeNetWorth,
  selectInvestmentObjectives,
  selectOATransactionTypes,
  selectMultiTransactionTypes,
  selectIsProspect,
  selectSettlementFundChoice,
  selectIsMultiInheritanceFlow,
  selectIsInheritedIraFlow,
  selectInheritedIraTransactionTypes,
  selectCurrentTransaction,
  (
    personalStates: PersonalStates,
    contactInfo: IPersonalAddressState,
    employment: Partial<VgEmploymentState> | undefined,
    financial: FinancialDetailsState,
    invObj: { objectives: InvestmentObjectives },
    transactionTypes: ITransferTypes,
    multiTransactionTypes: ITransferTypes,
    isProspect: boolean,
    settlementFund: SettlementFundAoAEnum | null,
    isMultiInheritance: boolean,
    isInheritedIra: boolean,
    inheritedIraTransactionTypes: ITransferTypes,
    currentTransaction: TransactionState | null | undefined,
  ) => {
    let transferToTypes: ITransferTypes;
    switch (true) {
      case isInheritedIra:
        transferToTypes = inheritedIraTransactionTypes;
        break;
      case isMultiInheritance:
        transferToTypes = multiTransactionTypes;
        settlementFund = currentTransaction?.settlementFund ?? null;
        break;
      default:
        transferToTypes = transactionTypes;
    }
    return clientDataOAMapper(
      personalStates,
      contactInfo,
      employment,
      financial,
      invObj,
      transferToTypes,
      settlementFund,
      isProspect,
    );
  },
);

export const selectITOAFromStore = createSelector(
  selectITPersonalStates,
  selectMfePersonalAddress,
  selectEmploymentMfeDataByOwnerIndex(0),
  selectIncomeNetWorth,
  selectInvestmentObjectives,
  selectITOATransactionTypes,
  selectSettlementFundChoice,
  (
    personalStates: PersonalStates,
    contactInfo: IPersonalAddressState,
    employment: Partial<VgEmploymentState> | undefined,
    financial: FinancialDetailsState,
    invObj: { objectives: InvestmentObjectives },
    transactionTypes: ITransferTypes,
    settlementFund: SettlementFundAoAEnum | null,
  ) => {
    return clientDataOAMapper(
      personalStates,
      contactInfo,
      employment,
      financial,
      invObj,
      transactionTypes,
      settlementFund,
    );
  },
);
